import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import {
  CurationCheckCard,
  CurationVerdictPanel,
  cadenceError,
  cadencePayload,
  verifiedLine,
  VERIFY_HELP,
  type CurationCheck,
  type VerificationFields,
} from "../lib/curationCheck.js";

// ─────────────────────────────────────────────
// Types (mirror the hub guidance frontmatter shape)
// ─────────────────────────────────────────────

type GuidanceEntry = {
  name: string;
  agent_type: string;
  category: string;
  priority: number;
  scope_tags: string[];
  status: string; // accepted | draft
  enabled: boolean;
  series_id: string;
  description: string;
  related: string[];
  body?: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
  suppresses_series?: string;
  promotion_status?: string; // pending | accepted | rejected
  promotion_resolved_at?: string;
  promotion_note?: string;
  archive_reason?: string;
  archived_at?: string;
  /** ADR-0201 — when a curator last confirmed this entry. */
  verified_at?: string | null;
  verified_by_actor?: string | null;
  verify_every_days?: number | null;
  lapsed?: boolean;
};

type Meta = {
  agent_types: string[];
  categories: string[];
  scope_tags: { functional: string[]; capability: string[] };
};

type GateCase = { id: string; title: string; actual: unknown; expected: unknown; error?: string };

const SLUG_RE = /^[a-z0-9-]{3,64}$/;

function fmtDateTime(s: string | null | undefined): string {
  return s ? new Date(s).toLocaleString() : "—";
}

function GuidanceMarkdown({ body }: { body: string }) {
  return (
    <div className="knowledgeMarkdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  );
}

// ─────────────────────────────────────────────
// Entry editor (separate route) — mirrors AdminPlatformKnowledgeEditor,
// plus the ADR-0148 §3.7 Promote panel + promotion-status chip.
// ─────────────────────────────────────────────

export function TenantGuidanceEntryEditor() {
  const { name: routeName } = useParams<{ name: string }>();
  const isNew = routeName === undefined;
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [existing, setExisting] = useState<GuidanceEntry | null>(null);
  const [candidates, setCandidates] = useState<GuidanceEntry[]>([]);
  const [gateBlock, setGateBlock] = useState<GateCase[] | null>(null);
  // ADR-0201: the curation check refuses a save in place — a toast cannot be acted on.
  const [checkBlock, setCheckBlock] = useState<CurationCheck | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [fCadence, setFCadence] = useState("");
  const [cadenceMsg, setCadenceMsg] = useState<string | null>(null);
  const [entryVerification, setEntryVerification] = useState<VerificationFields | null>(null);
  const [verification, setVerification] = useState<{ check: CurationCheck } | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [relatedOffer, setRelatedOffer] = useState<string[]>([]);
  const [showPromote, setShowPromote] = useState(false);
  const [promoteNote, setPromoteNote] = useState("");
  const [promoteConfirmed, setPromoteConfirmed] = useState(false);

  const [fName, setFName] = useState("");
  const [fAgent, setFAgent] = useState("query");
  const [fCategory, setFCategory] = useState("design");
  const [fPriority, setFPriority] = useState(70);
  const [fTags, setFTags] = useState<string[]>([]);
  const [fStatus, setFStatus] = useState("accepted");
  const [fDescription, setFDescription] = useState("");
  const [fRelated, setFRelated] = useState<string[]>([]);
  const [fBody, setFBody] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const [m, list] = await Promise.all([
          apiFetch<Meta>("/api/tenant/admin/tenant-guidance-meta"),
          apiFetch<{ entries: GuidanceEntry[] }>("/api/tenant/admin/tenant-guidance"),
        ]);
        setMeta(m);
        setCandidates(list.entries.filter((e) => e.name !== routeName));
      } catch {
        /* pickers degrade */
      }
      if (isNew) return;
      try {
        const res = await apiFetch<{ entry: GuidanceEntry }>(`/api/tenant/admin/tenant-guidance/${routeName}`);
        const e = res.entry;
        setExisting(e);
        setFName(e.name); setFAgent(e.agent_type); setFCategory(e.category); setFPriority(e.priority);
        setFTags(e.scope_tags); setFStatus(e.status); setFDescription(e.description);
        setFRelated(e.related); setFBody(e.body ?? "");
        setFCadence(e.verify_every_days ? String(e.verify_every_days) : "");
        setEntryVerification({ verified_at: e.verified_at, verified_by_actor: e.verified_by_actor, lapsed: e.lapsed });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [routeName, isNew]);

  const nameValid = SLUG_RE.test(fName.trim());
  const descValid = fDescription.trim().length > 0 && fDescription.trim().length <= 200;
  const isCanon = fStatus === "accepted" || (existing?.status === "accepted" && existing?.enabled !== false);
  const scopeTagOptions = useMemo(
    () => [...(meta?.scope_tags.functional ?? []), ...(meta?.scope_tags.capability ?? [])],
    [meta]
  );

  function bodyPayload(overrideOverride?: string) {
    return {
      name: fName.trim(),
      agent_type: fAgent,
      category: fCategory,
      priority: fPriority,
      description: fDescription.trim(),
      body: fBody,
      scope_tags: fTags,
      status: fStatus,
      related: fRelated,
      ...(cadencePayload(fCadence) !== undefined ? { verify_every_days: cadencePayload(fCadence) } : {}),
      ...(overrideOverride ? { override_reason: overrideOverride } : {}),
    };
  }

  // Draft autosave: silent, debounced, only while the entry is a draft (no gate).
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (loading || isCanon || !nameValid || !descValid) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      void apiFetch("/api/tenant/admin/tenant-guidance", { method: "POST", body: bodyPayload() }).catch(() => {
        /* draft autosave failures are silent; surfaced on explicit save */
      });
    }, 900);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fAgent, fCategory, fPriority, fTags, fStatus, fDescription, fRelated, fBody, isCanon, nameValid, descValid, loading]);

  async function saveCanon(override?: string) {
    setBusy(true);
    setGateBlock(null);
    setCheckBlock(null);
    try {
      const res = await apiFetch<{ check: CurationCheck | null }>("/api/tenant/admin/tenant-guidance", {
        method: "POST",
        body: bodyPayload(override),
      });
      toast.push({ kind: "success", message: "Saved." });
      // ADR-0201 §8.2 — a `complements` finding is an offer, not an auto-add.
      const offer = (res.check?.findings ?? [])
        .filter((f) => f.relation === "complements" && !fRelated.includes(f.name))
        .map((f) => f.name);
      if (offer.length > 0) {
        setRelatedOffer(offer);
        return;
      }
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "curation_check") {
        const check = (e.data as { check?: CurationCheck } | undefined)?.check;
        if (check) {
          setCheckBlock(check);
          return;
        }
        toast.push({ kind: "error", message: e.message });
      } else if (e instanceof ApiRequestError && e.code === "eval_regression") {
        const detail = (e.data as { detail?: { failing_cases?: GateCase[] } } | undefined)?.detail;
        setGateBlock(detail?.failing_cases ?? []);
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : "Save failed" });
      }
    } finally {
      setBusy(false);
    }
  }

  /** ADR-0201 §3.5 — a fresh check against the tree as it is now. */
  async function loadVerification() {
    if (!routeName) return;
    setVerifyBusy(true);
    try {
      const res = await apiFetch<{ check: CurationCheck }>(`/api/tenant/admin/tenant-guidance/${routeName}/verification`);
      setVerification({ check: res.check });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not load verification" });
    } finally {
      setVerifyBusy(false);
    }
  }

  /** "Still right" — restamp, change nothing else. */
  async function markVerified() {
    if (!routeName) return;
    setVerifyBusy(true);
    try {
      const res = await apiFetch<{ entry: GuidanceEntry }>(`/api/tenant/admin/tenant-guidance/${routeName}/verify`, {
        method: "POST",
      });
      setEntryVerification({
        verified_at: res.entry.verified_at,
        verified_by_actor: res.entry.verified_by_actor,
        lapsed: res.entry.lapsed,
      });
      toast.push({ kind: "success", message: "Verified." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not verify" });
    } finally {
      setVerifyBusy(false);
    }
  }

  /** Accept the related-entry offer: one click, one save, no modal. */
  async function addRelated(names: string[]) {
    const next = [...fRelated, ...names.filter((n) => !fRelated.includes(n))];
    setFRelated(next);
    setRelatedOffer([]);
    try {
      await apiFetch("/api/tenant/admin/tenant-guidance", {
        method: "POST",
        body: { ...bodyPayload(), related: next },
      });
      toast.push({ kind: "success", message: "Related entries added." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not add related entries" });
    }
  }

  async function archive() {
    if (!routeName) return;
    const ok = await confirm({ title: "Archive entry?", detail: `Archive "${routeName}"? It leaves the index and stops loading into your agents.`, confirmLabel: "Archive" });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/admin/tenant-guidance/${routeName}/archive`, { method: "POST", body: { reason: "archived from console" } });
      toast.push({ kind: "success", message: "Archived." });
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Archive failed" });
    }
  }

  async function toggleEnabled() {
    if (!routeName || !existing) return;
    try {
      await apiFetch(`/api/tenant/admin/tenant-guidance/${routeName}/enabled`, { method: "POST", body: { enabled: existing.enabled === false } });
      toast.push({ kind: "success", message: existing.enabled === false ? "Enabled." : "Disabled." });
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Failed" });
    }
  }

  async function promote() {
    if (!routeName) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/tenant-guidance/${routeName}/promote`, {
        method: "POST",
        body: { note: promoteNote.trim() || undefined, confidentiality_confirmed: true },
      });
      toast.push({ kind: "success", message: "Submitted for platform review." });
      setShowPromote(false);
      // Reload to show the pending chip.
      const res = await apiFetch<{ entry: GuidanceEntry }>(`/api/tenant/admin/tenant-guidance/${routeName}`);
      setExisting(res.entry);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Promotion failed" });
    } finally {
      setBusy(false);
    }
  }

  async function archiveAfterAccepted() {
    if (!routeName) return;
    const ok = await confirm({
      title: "Archive the local copy?",
      detail: `"${routeName}" is now platform-wide guidance. Archive this tenant copy so it isn't duplicated?`,
      confirmLabel: "Archive local copy",
    });
    if (!ok) return;
    await archive();
  }

  const canPromote =
    !isNew && existing && existing.status === "accepted" && existing.enabled !== false && !existing.suppresses_series;

  const promotionChip = (() => {
    const s = existing?.promotion_status;
    if (s === "pending") return <span className="pill pillWarn" title="Awaiting a hub admin's review.">Pending platform review</span>;
    if (s === "accepted") return <span className="pill pillOk">Accepted as platform knowledge</span>;
    if (s === "rejected") return <span className="pill pillBad" title={existing?.promotion_note || ""}>Not accepted</span>;
    return null;
  })();

  const title = (
    <>
      <Link to="/app/admin/tenant-knowledge">Tenant Knowledge</Link>
      {" / "}
      {isNew ? "New guidance entry" : routeName}
    </>
  );

  if (error) return <PageLayout title={title}><ErrorState detail={error} /></PageLayout>;
  if (loading) return <PageLayout title={title}><LoadingState /></PageLayout>;

  return (
    <PageLayout
      title={title}
      actions={
        <>
          <button className="btn" disabled={busy} onClick={() => navigate("/app/admin/tenant-knowledge")}>Back</button>
          {!isNew && existing && (
            <button className="btn" disabled={busy} onClick={() => void toggleEnabled()}>{existing.enabled === false ? "Enable" : "Disable"}</button>
          )}
          {!isNew && (
            <button className="btn btnDanger" disabled={busy} onClick={() => void archive()}>Archive</button>
          )}
          {isCanon ? (
            <button className="btn btnPrimary" disabled={busy || !nameValid || !descValid} onClick={() => void saveCanon()}>
              {busy ? "Validating…" : "Validate & save"}
            </button>
          ) : (
            <span className="pill pillWarn" title="Drafts autosave; no eval gate.">draft · autosaving</span>
          )}
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {isCanon && (
          <div className="help">
            Accepted entries load into your workspace's agents. Saving runs the eval gate — a write that regresses a previously-passing
            case is refused. Switch Status to <strong>draft</strong> to iterate without the gate (drafts autosave).
          </div>
        )}

        {/* Promotion status + Promote-to-platform (§3.7). */}
        {!isNew && existing && (
          <div className="card" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span className="label" style={{ margin: 0 }}>Platform promotion</span>
            {promotionChip ?? <span className="muted" style={{ fontSize: 12 }}>Not promoted</span>}
            {existing.promotion_status === "accepted" && (
              <button className="btn" disabled={busy} onClick={() => void archiveAfterAccepted()}>Archive this local copy</button>
            )}
            {canPromote && existing.promotion_status !== "pending" && !showPromote && (
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => { setShowPromote(true); setPromoteConfirmed(false); setPromoteNote(""); }}>
                Promote to platform…
              </button>
            )}
          </div>
        )}
        {showPromote && (
          <div className="card" style={{ display: "grid", gap: 10, borderColor: "var(--border)" }}>
            <div style={{ fontWeight: 600 }}>Promote to platform</div>
            <div className="help">
              This proposes the entry as platform-wide guidance for a hub admin to review. It is quarantined until accepted —
              it does not immediately affect other tenants. You'll see the outcome here.
            </div>
            <label className="field">
              <span className="label">Note for the reviewer (optional)</span>
              <input className="input" value={promoteNote} onChange={(e) => setPromoteNote(e.target.value)} placeholder="Why this is broadly useful" />
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13 }}>
              <input type="checkbox" checked={promoteConfirmed} onChange={(e) => setPromoteConfirmed(e.target.checked)} />
              <span>I confirm this content may be shared platform-wide and contains nothing confidential to this workspace.</span>
            </label>
            <div className="actions">
              <button className="btn" disabled={busy} onClick={() => setShowPromote(false)}>Cancel</button>
              <button className="btn btnPrimary" disabled={busy || !promoteConfirmed} onClick={() => void promote()}>Submit for review</button>
            </div>
          </div>
        )}

        {checkBlock && (
          <CurationCheckCard
            check={checkBlock}
            surface="guidance"
            overrideReason={overrideReason}
            onOverrideReasonChange={setOverrideReason}
            onCancel={() => setCheckBlock(null)}
            onOverride={() => void saveCanon(overrideReason.trim())}
            busy={busy}
          />
        )}
        {relatedOffer.length > 0 && (
          <div className="card curationRelatedLine">
            <span>Related to {relatedOffer.join(", ")}.</span>
            <button className="btn btnSm" onClick={() => void addRelated(relatedOffer)}>Add</button>
            <button className="adminListAction" onClick={() => { setRelatedOffer([]); navigate("/app/admin/tenant-knowledge"); }}>
              Not now
            </button>
          </div>
        )}
        {gateBlock && (
          <div className="card" style={{ borderColor: "var(--danger-border)", background: "var(--danger-bg)", display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Eval gate blocked this save — {gateBlock.length} case{gateBlock.length === 1 ? "" : "s"} regressed:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {gateBlock.map((c) => <li key={c.id} style={{ fontSize: 13 }}>{c.title}{c.error ? ` — ${c.error}` : ""}</li>)}
            </ul>
            <label className="field">
              <span className="label">Override reason (saves anyway, recorded to the audit)</span>
              <input className="input" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Why this is safe to override" />
            </label>
            <div className="actions">
              <button className="btn" onClick={() => setGateBlock(null)}>Cancel</button>
              <button className="btn btnDanger" disabled={busy || !overrideReason.trim()} onClick={() => void saveCanon(overrideReason.trim())}>Override & save</button>
            </div>
          </div>
        )}

        <div className="knowledgeEditorLayout">
          <div className="card" style={{ display: "grid", gap: 10, alignSelf: "start", alignContent: "start" }}>
            <label className="field">
              <span className="label">Name</span>
              <input className="input mono" maxLength={64} value={fName} disabled={!isNew} onChange={(e) => setFName(e.target.value)} placeholder="kebab-case-slug" />
              <div className="help">{isNew ? "Lowercase letters, digits, hyphens (3–64). Permanent." : "Names are permanent — to rename, archive and recreate."}</div>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label className="field">
                <span className="label">Agent</span>
                <select className="select" value={fAgent} onChange={(e) => setFAgent(e.target.value)}>
                  {(meta?.agent_types ?? [fAgent]).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </label>
              <label className="field">
                <span className="label">Category</span>
                <select className="select" value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                  {(meta?.categories ?? [fCategory]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label className="field">
                <span className="label">Priority</span>
                <input className="input" type="number" min={1} max={999} value={fPriority} onChange={(e) => setFPriority(Number(e.target.value))} />
                <div className="help">51–99 design · 100–199 reference · 200+ anti-pattern</div>
              </label>
              <label className="field">
                <span className="label">Status</span>
                <select className="select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                  <option value="accepted">accepted</option>
                  <option value="draft">draft</option>
                </select>
              </label>
            </div>
            <label className="field">
              <span className="label">Verify every (days)</span>
              <input
                className="input"
                type="number"
                min={1}
                max={365}
                value={fCadence}
                onChange={(e) => setFCadence(e.target.value)}
                onBlur={() => setCadenceMsg(cadenceError(fCadence))}
                placeholder="90"
              />
              <div className="help">{cadenceMsg ?? VERIFY_HELP.cadenceField}</div>
            </label>
            <label className="field">
              <span className="label">Description</span>
              <textarea className="input" rows={2} maxLength={200} style={{ resize: "vertical" }} value={fDescription} onChange={(e) => setFDescription(e.target.value)} placeholder="One line — the hook shown in the index (≤200)" />
            </label>
            <div className="field">
              <span className="label">Scope tags</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                {fTags.map((t) => (
                  <span key={t} className="pill pillOk" style={{ fontSize: 11, cursor: "pointer" }} title="Remove" onClick={() => setFTags(fTags.filter((x) => x !== t))}>{t} ×</span>
                ))}
                <select className="select" style={{ fontSize: 12 }} value="" onChange={(e) => { if (e.target.value && !fTags.includes(e.target.value)) setFTags([...fTags, e.target.value]); e.target.value = ""; }}>
                  <option value="">+ add</option>
                  {scopeTagOptions.filter((t) => !fTags.includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="help">Empty = loads everywhere. Otherwise a functional tag or a node-type capability tag.</div>
            </div>
            <div className="field">
              <span className="label">Related entries</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                {fRelated.map((r) => (
                  <span key={r} className="pill" style={{ fontSize: 11, cursor: "pointer" }} title="Remove" onClick={() => setFRelated(fRelated.filter((x) => x !== r))}>{r} ×</span>
                ))}
                <select className="select" style={{ fontSize: 12 }} value="" onChange={(e) => { if (e.target.value && !fRelated.includes(e.target.value)) setFRelated([...fRelated, e.target.value]); e.target.value = ""; }}>
                  <option value="">+ add</option>
                  {candidates.filter((c) => !fRelated.includes(c.name)).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {existing && (
              <div className="muted" style={{ fontSize: 12 }}>
                Updated {fmtDateTime(existing.updated_at)} by {existing.updated_by.replace(/^(account|key|capture|escalation|system):/, "")}
              </div>
            )}
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <span className="label">Body</span>
              <span className="muted" style={{ fontSize: 12 }}>Markdown — SQL in ```sql fences. Preview on the right.</span>
            </div>
            <div className="knowledgeEditorSplit">
              <textarea className="input" value={fBody} onChange={(e) => setFBody(e.target.value)} placeholder={"State the rule or technique…\n\n```sql\nSELECT ...\n```"} />
              <div className="knowledgeMarkdownPane"><GuidanceMarkdown body={fBody || "*Nothing to preview yet.*"} /></div>
            </div>
          </div>
        </div>

        {!isNew && (
          <div className="card" style={{ display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Verification</div>
            <div className="help">{VERIFY_HELP.column}</div>
            <div style={{ fontSize: 13 }}>
              {entryVerification ? verifiedLine(entryVerification) : "Never verified"}
              {entryVerification?.lapsed ? <> &middot; <span className="pill pillWarn">lapsed</span></> : null}
            </div>
            {verification ? (
              <CurationVerdictPanel check={verification.check} surface="guidance" />
            ) : (
              <div className="help">Check this entry against the guidance as it is now before confirming it.</div>
            )}
            <div className="actions">
              {!verification && (
                <button className="btn btnSm" disabled={verifyBusy} onClick={() => void loadVerification()}>
                  {verifyBusy ? "Checking…" : "Check against current guidance"}
                </button>
              )}
              <button className="btn btnSm" disabled={verifyBusy} onClick={() => void markVerified()}>Still right</button>
              <button className="btn btnSm isDanger" disabled={busy} onClick={() => void archive()}>Archive</button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
